/**
 * A collection read a page at a time, accumulating what has been seen.
 *
 * Every paged screen in the console needs the same four things — the rows so
 * far, whether the platform said there are more, a way to ask for the next
 * page, and a way to start again — and each one that reimplements them gets a
 * slightly different answer to the questions that matter: whether the second
 * page appends or replaces, whether a refresh keeps the pages before it, and
 * what happens when a superseded request comes back late.
 *
 * So the pagination lives here and knows nothing about what it is paging.
 * It is handed a function that reads one page and does not know the path, the
 * page size, the capability, or which of Brains, Memory or Authorization it is
 * serving. Those are the screen's business, and a primitive that knew any of
 * them would have to be edited every time a screen did.
 *
 * Staleness is not defended against here, because it is already defended
 * against properly: `useResource` treats the request as an identity and ignores
 * any answer that is not the one it is currently waiting for, and aborts the
 * request it superseded. Changing the cursor changes that identity, so a page
 * that arrives after a refresh is discarded by the same mechanism that discards
 * any other superseded read, rather than by a second bespoke guard here.
 */

import { useCallback, useMemo, useState } from "react";
import { useResource, type Resource } from "./useResource";

/** The page shape the platform returns, named without naming an endpoint. */
export interface PageOf<T> {
  items: T[];
  next_cursor: string | null;
  has_more: boolean;
}

/** One identity for "nothing accumulated", so a render does not look like a change. */
const NOTHING: readonly never[] = [];

export interface PagedCollection<T> {
  /** The state of the page currently being read — loading, refused, ready. */
  state: Resource<PageOf<T>>;
  /** Everything accumulated so far, in the order the platform returned it. */
  items: T[];
  hasMore: boolean;
  /** Ask for the next page. Does nothing when there is not one. */
  more: () => void;
  /** Read again from the first page, discarding what was accumulated. */
  refresh: () => void;
}

/**
 * `readPage` must be stable — wrap it in `useCallback` — because it is part of
 * the identity of the request, exactly as `useResource` requires.
 *
 * `resetKey` is what the accumulated pages belong to. When it changes, the
 * collection starts at the beginning rather than showing the previous
 * subject's rows under the new subject's heading. A screen paging one fixed
 * collection can leave it alone.
 */
export function usePagedCollection<T>(
  readPage: (cursor: string | null, signal: AbortSignal) => Promise<PageOf<T>>,
  options: { enabled?: boolean; resetKey?: string } = {},
): PagedCollection<T> {
  const enabled = options.enabled ?? true;
  const resetKey = options.resetKey ?? "";

  // The position is remembered against the key it belongs to, so a changed key
  // is a fresh collection without needing an effect to notice and reset it.
  const [at, setAt] = useState<{ key: string; cursor: string | null; earlier: T[] }>({
    key: resetKey,
    cursor: null,
    earlier: [],
  });
  const here = at.key === resetKey;
  const cursor = here ? at.cursor : null;
  const earlier: readonly T[] = here ? at.earlier : NOTHING;

  const read = useCallback(
    (signal: AbortSignal) => readPage(cursor, signal),
    [readPage, cursor],
  );
  const { state, reload } = useResource<PageOf<T>>(read, { enabled });

  // Appended, never replaced: the pages already seen, then the page in hand.
  // While a later page is loading the earlier ones stay on screen, because a
  // collection that emptied itself to fetch its own next page would be worse
  // than one that did not paginate at all.
  const items = useMemo(
    () => (state.kind === "ready" ? [...earlier, ...state.value.items] : [...earlier]),
    [state, earlier],
  );

  const more = useCallback(() => {
    if (state.kind !== "ready") return;
    // `has_more` is the platform's own answer about whether another page
    // exists, and it is the only thing consulted here — there is no second
    // flag kept alongside it that could disagree. The cursor is checked as
    // well because a page that claims more without saying where would
    // otherwise be asked for again from the beginning, silently re-reading
    // page one forever.
    if (!state.value.has_more || state.value.next_cursor === null) return;
    setAt({ key: resetKey, cursor: state.value.next_cursor, earlier: items });
  }, [state, resetKey, items]);

  const refresh = useCallback(() => {
    setAt({ key: resetKey, cursor: null, earlier: [] });
    // Already at the first page, so nothing about the request identity changed
    // and only an explicit re-read will ask again.
    if (cursor === null) reload();
  }, [resetKey, cursor, reload]);

  return {
    state,
    items,
    hasMore: state.kind === "ready" && state.value.has_more,
    more,
    refresh,
  };
}
